import { useMemo } from 'react'

import { InventoryComparisonRow } from '../../types/inventoryHealth'

export type ColumnFilters = {
  roomType: string
  ratePlan: string
  channelName: string
  assessment: string
}

export const EMPTY_COLUMN_FILTERS: ColumnFilters = {
  roomType: '',
  ratePlan: '',
  channelName: '',
  assessment: ''
}

/** The value shown to the operator, so the filter matches what they read. */
export function roomTypeOf(row: InventoryComparisonRow): string {
  return row.roomTypeName || row.roomTypeCode
}

export function ratePlanOf(row: InventoryComparisonRow): string {
  return row.ratePlanName || row.ratePlanCode
}

export function channelOf(row: InventoryComparisonRow): string {
  return row.channelName || row.channelID
}

/**
 * Distinct values for one column, from the rows actually loaded.
 *
 * Deriving the options from the data rather than from a fixed list means a
 * filter can never offer a value that matches nothing — an empty result from a
 * filter the screen itself offered reads as a broken screen.
 */
function optionsFor(
  rows: InventoryComparisonRow[],
  read: (row: InventoryComparisonRow) => string
): string[] {
  const seen = new Set<string>()
  rows.forEach((row) => {
    const value = read(row)
    if (value !== '') {
      seen.add(value)
    }
  })

  return Array.from(seen).sort((a, b) => a.localeCompare(b))
}

export type ColumnFilterOptions = {
  roomTypes: string[]
  ratePlans: string[]
  channels: string[]
  assessments: string[]
}

/**
 * Narrows the loaded rows for READING. This is a different act from the filter
 * panel, which changes what is fetched and costs a PMS round trip — so these
 * apply immediately and never re-query.
 *
 * Filtering client-side is honest only because the query asks for the server's
 * maximum page. Where the server still truncated, the caller says so; a filter
 * that silently searched one page of many would be worse than no filter.
 */
export function useComparisonRowFilters(
  rows: InventoryComparisonRow[],
  filters: ColumnFilters
) {
  const options = useMemo<ColumnFilterOptions>(
    () => ({
      roomTypes: optionsFor(rows, roomTypeOf),
      ratePlans: optionsFor(rows, ratePlanOf),
      channels: optionsFor(rows, channelOf),
      assessments: optionsFor(rows, (row) =>
        row.findingCodes.length === 0 ? 'Agrees' : row.status
      )
    }),
    [rows]
  )

  const filtered = useMemo(
    () =>
      rows.filter((row) => {
        if (filters.roomType !== '' && roomTypeOf(row) !== filters.roomType) {
          return false
        }
        if (filters.ratePlan !== '' && ratePlanOf(row) !== filters.ratePlan) {
          return false
        }
        if (
          filters.channelName !== '' &&
          channelOf(row) !== filters.channelName
        ) {
          return false
        }
        if (filters.assessment !== '') {
          const assessment =
            row.findingCodes.length === 0 ? 'Agrees' : row.status
          if (assessment !== filters.assessment) {
            return false
          }
        }

        return true
      }),
    [rows, filters]
  )

  return { filtered, options }
}
